import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { wsClient } from '@/lib/ws';
import { useAuth } from '@/lib/auth';
import { FireSimple, Sparkle, Lightning, Megaphone, Eye, Gift, Ghost, Users } from '@phosphor-icons/react';

/**
 * Subscribes to the WebSocket and renders toast notifications for live events.
 * Mounts inside AppShell once an authenticated user is present.
 */
const LiveEvents = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    wsClient.start();
    const off = wsClient.onEvent((evt) => {
      if (!evt || !evt.type) return;
      const p = evt.payload || {};
      if (evt.type === 'burst') {
        toast.custom(() => (
          <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`live-burst-${p.id}`}>
            <FireSimple size={20} weight="fill" className="text-bx-legendary" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">{p.title || 'Burst event'}</div>
              <div className="text-xs text-white/60 truncate">{p.subtitle || 'Network igniting'}</div>
            </div>
          </div>
        ), { duration: 4200 });
      } else if (evt.type === 'transfer') {
        if (p.to === user.username || p.from === user.username) return;
        toast.custom(() => (
          <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3 max-w-sm" data-testid="live-transfer">
            <Sparkle size={18} weight="fill" className="text-bx-cyan" />
            <div className="flex-1 min-w-0 text-sm">
              <span className="text-bx-cyan font-bold">@{p.from}</span>
              <span className="text-white/55"> passed energy to </span>
              <span className="text-bx-magenta font-bold">@{p.to}</span>
            </div>
          </div>
        ), { duration: 3500 });
      } else if (evt.type === 'sponsor_live') {
        toast.custom(() => (
          <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3" data-testid="live-sponsor">
            <Megaphone size={18} weight="fill" className="text-bx-volt" />
            <div className="text-sm font-bold">A new sponsor went live in the feed.</div>
          </div>
        ), { duration: 4000 });
      } else if (evt.type === 'bubble_observed') {
        toast.custom(() => (
          <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3 max-w-sm" data-testid="live-observation">
            <Eye size={18} weight="fill" className="text-bx-cyan" />
            <div className="flex-1 min-w-0 text-sm">
              <div className="text-bx-cyan font-bold text-xs">{p.name} just saw something</div>
              <div className="text-white/70 italic text-xs truncate">"{p.text}"</div>
            </div>
          </div>
        ), { duration: 5000 });
      } else if (evt.type === 'random_gift') {
        if (p.to === user.username) {
          toast.custom(() => (
            <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3 max-w-sm border border-bx-gold/40" data-testid="live-gift-received">
              <Gift size={18} weight="fill" className="text-bx-gold" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="text-bx-gold font-bold text-xs">@{p.by} surprised you</div>
                <div className="text-white/70 text-xs">{p.kind === 'energy' ? `+${p.amount}⚡ energy` : p.kind === 'xp-bonus' ? `+${p.amount} XP` : '✨ trinket gift'}</div>
              </div>
            </div>
          ), { duration: 6000 });
        } else if (p.by !== user.username) {
          toast.custom(() => (
            <div className="glass-hi rounded-xl px-3 py-2 flex items-center gap-2 text-xs" data-testid="live-gift-network">
              <Gift size={14} weight="fill" className="text-bx-gold" />
              <span className="text-bx-gold">@{p.by}</span><span className="text-white/55">→</span><span className="text-bx-magenta">@{p.to}</span>
            </div>
          ), { duration: 3000 });
        }
      } else if (evt.type === 'ghost_spawned') {
        toast.custom(() => (
          <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3" data-testid="live-ghost-spawned"
               style={{ borderColor: 'rgba(255,215,0,0.4)', borderWidth: 1 }}>
            <Ghost size={20} weight="fill" className="text-bx-gold animate-pulse" />
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-bold text-bx-gold">{p.name} is roaming the network.</div>
              <div className="text-white/60 text-xs">first to catch it keeps it.</div>
            </div>
          </div>
        ), { duration: 8000 });
      } else if (evt.type === 'ghost_caught') {
        toast.custom(() => (
          <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3" data-testid="live-ghost-caught"
               style={{ background: 'linear-gradient(135deg, #ffd70022, #ff007f22)' }}>
            <Ghost size={20} weight="fill" className="text-bx-gold" />
            <div className="text-sm font-bold">
              <span className="text-bx-magenta">@{p.by}</span> caught <span className="text-bx-gold">{p.name}</span>.
            </div>
          </div>
        ), { duration: 6000 });
      } else if (evt.type === 'circle_step' || evt.type === 'circle_complete') {
        toast.custom(() => (
          <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3" data-testid="live-circle">
            <Users size={18} weight="fill" className="text-bx-volt" />
            <div className="text-sm">
              {evt.type === 'circle_complete'
                ? <span><span className="text-bx-volt font-bold">{p.bubble_name}</span> closed a circle of {p.size}.</span>
                : <span><span className="text-bx-volt font-bold">{p.bubble_name}</span> passing through · {p.remaining} left</span>}
            </div>
          </div>
        ), { duration: 4000 });
      } else if (evt.type === 'tribute_premium') {
        toast.custom(() => (
          <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3" data-testid="live-tribute">
            <span className="text-2xl">{p.emoji}</span>
            <div className="text-sm">
              <span className="text-bx-magenta font-bold">@{p.by}</span>
              <span className="text-white/55"> left a </span>
              <span className="text-bx-gold font-bold">{p.tier}</span>
            </div>
          </div>
        ), { duration: 4000 });
      } else if (evt.type === 'bloodline_spawned') {
        if (p.to_username === user.username) {
          toast.custom(() => (
            <div className="glass-hi rounded-xl px-4 py-3 flex items-center gap-3 max-w-sm"
                 style={{ borderColor: 'rgba(255,215,0,0.4)', borderWidth: 1 }}
                 data-testid="live-bloodline">
              <Sparkle size={18} weight="fill" className="text-bx-gold" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-bold text-bx-gold">{p.parent_name}'s pulse continues.</div>
                <div className="text-white/70 text-xs">{p.child_name} was born from the lineage.</div>
              </div>
            </div>
          ), { duration: 8000 });
        }
      }
    });
    return () => { off(); wsClient.stop(); };
  }, [user]);
  return null;
};

export const PresencePill = ({ count }) => (
  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-bx-volt" data-testid="presence-pill">
    <span className="relative inline-flex h-2 w-2">
      <span className="absolute inset-0 rounded-full bg-bx-volt animate-ping opacity-75"></span>
      <span className="relative inline-flex rounded-full h-2 w-2 bg-bx-volt"></span>
    </span>
    <Lightning size={10} weight="fill" />
    <span>{count.toLocaleString()} online</span>
  </div>
);

export default LiveEvents;
